import { useState } from "react";
import { Link } from "react-router-dom";
import { bankInfoCardId, businessInfoCardId, ctaBusinessSettingId, personalInfoCardId } from "@/assets/googleTrackIds";
import { DashboardRoutes } from "@/router";
import { useAppDispatch, useAppSelector, useAppTranslation } from "@/utils";
import {
  DashboardChecksTypeEnum,
  IpgHelperWithdrawStatusEnum,
  WizardStatusEnum,
  terminalManagerApis,
} from "@payping/api";
import { CheckCircleOutlined, CloseCircleOutlined, PhRClockCountdown } from "@payping/icons";
import {
  Button,
  Collapse,
  Flex,
  LegacyCard,
  Skeleton,
  Steps,
  Typography,
  usePaypingContext,
} from "@payping/web-components";
import { buildUrl } from "@payping/utilities-shared/url";
import { getUserChecks } from "@payping/redux/actions";
import { useAsync } from "react-use";
import { URLS } from "@payping/utilities-shared/consts";
import { useGetLastWithdrawByStatus } from "@/pages/Sale/Withdraw/_hooks";
import { MerchantIbanStatusEnum } from "@payping/api/app/api/terminalmanagerTypes";
import { UserSignContract } from "./sign-contract";

enum StepsEnum {
  identityInformation = 0,
  businessInformation = 1,
  bankInformation = 2,
}

interface Props {
  isLoading: boolean;
}
const CompleteProfile = ({ isLoading }: Props) => {
  const { t } = useAppTranslation();
  const { breakpoints } = usePaypingContext();
  const { xl } = breakpoints;
  const dispatch = useAppDispatch();

  const checks = useAppSelector((state) => state.user.checks);
  const haveTerminal = useAppSelector((state) => state.user?.personalInfo?.haveTerminal);
  const tasksStatus = useAppSelector((state) => state.user.overview.tasksStatus);
  const wizard = useAppSelector((state) => state.user.wizard);
  const lastWithdarwState = useGetLastWithdrawByStatus(IpgHelperWithdrawStatusEnum.Succeed);

  const hasValidContract = useAppSelector((state) => state.user.hasValidContract);

  const { loading: loadingChecks } = useAsync(async () => {
    await dispatch(getUserChecks());
  }, [dispatch]);
  const { loading: loadingBank, value: bankSectionStatus = MerchantIbanStatusEnum.Init } = useAsync(async () => {
    const response = await terminalManagerApis.getIbans(1, 50);
    const status = response.list?.items?.reduce(
      (acc, item) => Math.max(acc, item.status || MerchantIbanStatusEnum.Init),
      MerchantIbanStatusEnum.Init,
    ) as MerchantIbanStatusEnum;
    return status;
  });

  const loading = isLoading || loadingChecks || loadingBank;

  const hideSection =
    tasksStatus?.hasCoupon &&
    tasksStatus.hasCustomer &&
    tasksStatus.hasInvoices &&
    tasksStatus.hasProduct &&
    tasksStatus.hasToken &&
    tasksStatus.hasTransaction;

  const personalSectionStatus = Math.min(
    Number(checks?.personalStatus || 0),
    Number(checks?.birthCertificateStatus || 0),
  );
  const businessSectionStatus = Math.min(
    checks?.isSiteRequired ? Number(checks.siteStatus || 0) : DashboardChecksTypeEnum.Confirmed,
    Number(checks?.businessStatus || 0),
    Number(checks?.businessRegistrationStatus || 0),
    Number(checks?.localStatus || 0),
  );

  const isProfileCompleted =
    personalSectionStatus === DashboardChecksTypeEnum.Confirmed &&
    businessSectionStatus === DashboardChecksTypeEnum.Confirmed &&
    bankSectionStatus === MerchantIbanStatusEnum.Accepted;

  const [currentStep, setCurrentStep] = useState(() => {
    if (personalSectionStatus === DashboardChecksTypeEnum.NotFilled) {
      return StepsEnum.identityInformation;
    } else if (businessSectionStatus === DashboardChecksTypeEnum.NotFilled) {
      return StepsEnum.businessInformation;
    } else if (
      bankSectionStatus === MerchantIbanStatusEnum.Init ||
      bankSectionStatus === MerchantIbanStatusEnum.Failed
    ) {
      return StepsEnum.bankInformation;
    } else {
      if (personalSectionStatus === DashboardChecksTypeEnum.AdminPending) {
        return StepsEnum.identityInformation;
      } else if (businessSectionStatus === DashboardChecksTypeEnum.AdminPending) {
        return StepsEnum.businessInformation;
      } else if (bankSectionStatus === MerchantIbanStatusEnum.Pending) {
        return StepsEnum.bankInformation;
      } else {
        return StepsEnum.identityInformation;
      }
    }
  });

  const itemSubTitle = (status: number) => {
    switch (status) {
      case DashboardChecksTypeEnum.NotFilled:
        return t("overview.completeProfile.status.notFilled");
      case DashboardChecksTypeEnum.AdminPending:
        return t("overview.completeProfile.status.pending");
      case DashboardChecksTypeEnum.Confirmed:
        return t("overview.completeProfile.status.confirmed");
      default:
        return undefined;
    }
  };
  const itemIcon = (status: number) => {
    switch (status) {
      case DashboardChecksTypeEnum.NotFilled:
        return <CloseCircleOutlined width={"16px"} className="color--danger" />;
      case DashboardChecksTypeEnum.AdminPending:
        return <PhRClockCountdown width={"16px"} className="color--warning" />;
      case DashboardChecksTypeEnum.Confirmed:
        return <CheckCircleOutlined width={"16px"} className="color--success" />;
      default:
        return undefined;
    }
  };
  const bankItemSubTitle = (status: MerchantIbanStatusEnum) => {
    switch (status) {
      case MerchantIbanStatusEnum.Init:
      case MerchantIbanStatusEnum.Failed:
        return t("overview.completeProfile.status.notFilled");
      case MerchantIbanStatusEnum.Pending:
        return t("overview.completeProfile.status.pending");
      case MerchantIbanStatusEnum.Accepted:
        return t("overview.completeProfile.status.confirmed");
      default:
        return undefined;
    }
  };
  const bankItemIcon = (status: MerchantIbanStatusEnum) => {
    switch (status) {
      case MerchantIbanStatusEnum.Init:
      case MerchantIbanStatusEnum.Failed:
        return <CloseCircleOutlined width={"16px"} className="color--danger" />;
      case MerchantIbanStatusEnum.Pending:
        return <PhRClockCountdown width={"16px"} className="color--warning" />;
      case MerchantIbanStatusEnum.Accepted:
        return <CheckCircleOutlined width={"16px"} className="color--success" />;
      default:
        return undefined;
    }
  };
  const identityInformationContent = (
    <Flex vertical className={"tw:xl:mt-4"}>
      <Typography.Title level={3}>{t(`overview.completeProfile.identityInformation.title`)}</Typography.Title>
      <Typography.Paragraph className="color--gray500">
        {t(`overview.completeProfile.identityInformation.content`)}
      </Typography.Paragraph>
      {isProfileCompleted ? null : (
        <div>
          {personalSectionStatus === DashboardChecksTypeEnum.Confirmed ? (
            <Button disabled type="primary">
              {t(`overview.completeProfile.identityInformation.actions.edit`)}
            </Button>
          ) : (
            <Link
              to={
                personalSectionStatus === DashboardChecksTypeEnum.AdminPending
                  ? DashboardRoutes.settings.personal.edit.url
                  : DashboardRoutes.register.personal.url
              }
              data-google-track={personalInfoCardId}
            >
              <Button type="primary">
                {t(
                  `overview.completeProfile.identityInformation.actions.${
                    personalSectionStatus === DashboardChecksTypeEnum.AdminPending ? "edit" : "submit"
                  }`,
                )}
              </Button>
            </Link>
          )}
        </div>
      )}
    </Flex>
  );
  const businessInformationContent = (
    <Flex vertical className={"tw:xl:mt-4"}>
      <Typography.Title level={3}>{t(`overview.completeProfile.businessInformation.title`)}</Typography.Title>
      <Typography.Paragraph className="color--gray500">
        {t(`overview.completeProfile.businessInformation.content`)}
      </Typography.Paragraph>
      {isProfileCompleted ? null : (
        <div>
          {businessSectionStatus === DashboardChecksTypeEnum.Confirmed ? (
            <Button disabled type="primary">
              {t(`overview.completeProfile.businessInformation.actions.edit`)}
            </Button>
          ) : (
            <Link
              to={
                businessSectionStatus === DashboardChecksTypeEnum.AdminPending
                  ? DashboardRoutes.settings.business.edit.url
                  : DashboardRoutes.register.business.url
              }
              data-google-track={businessInfoCardId}
            >
              <Button type="primary">
                {t(
                  `overview.completeProfile.businessInformation.actions.${
                    businessSectionStatus === DashboardChecksTypeEnum.AdminPending ? "edit" : "submit"
                  }`,
                )}
              </Button>
            </Link>
          )}
        </div>
      )}
    </Flex>
  );
  const bankInformationContent = (
    <Flex vertical className={"tw:xl:mt-4"}>
      <Typography.Title level={3}>{t(`overview.completeProfile.bankInformation.title`)}</Typography.Title>
      <Typography.Paragraph className="color--gray500">
        {t(`overview.completeProfile.bankInformation.content`)}
      </Typography.Paragraph>
      {isProfileCompleted ? null : (
        <div>
          {bankSectionStatus === MerchantIbanStatusEnum.Accepted ? (
            <Button disabled type="primary">
              {t(`overview.completeProfile.bankInformation.actions.edit`)}
            </Button>
          ) : (
            <Link
              to={
                bankSectionStatus === MerchantIbanStatusEnum.Pending
                  ? DashboardRoutes.settings.bank.edit.url
                  : DashboardRoutes.register.bank.url
              }
              data-google-track={bankInfoCardId}
            >
              <Button type="primary">
                {t(
                  `overview.completeProfile.bankInformation.actions.${
                    bankSectionStatus === MerchantIbanStatusEnum.Pending ? "edit" : "submit"
                  }`,
                )}
              </Button>
            </Link>
          )}
        </div>
      )}
    </Flex>
  );

  const successfulTerminalCard = (
    <LegacyCard.Ant className={"tw:w-full"}>
      <Flex vertical gap="12px">
        <Typography.Text className="color--gray500 tw:mb-4">
          {t("overview.completeProfile.informationConfirmed.upperTitle")}
        </Typography.Text>
        <CheckCircleOutlined className="color--success" width={"36px"} />
        <Typography.Title className="tw:m-0!" level={4}>
          {t("overview.completeProfile.informationConfirmed.title")}
        </Typography.Title>
        <Typography.Text className="color--gray500">
          {t("overview.completeProfile.informationConfirmed.content")}
        </Typography.Text>
        <Link to={DashboardRoutes.settings.business.root.url} data-google-track={ctaBusinessSettingId}>
          <Button>{t("overview.completeProfile.informationConfirmed.actions.BusinessSettings")}</Button>
        </Link>
      </Flex>
    </LegacyCard.Ant>
  );
  const pendingTerminalCard = (
    <LegacyCard.Ant className={"tw:w-full"}>
      <Flex align={"center"} gap={"small"}>
        <img
          alt={"Pending Shaparak"}
          width={80}
          height={80}
          src={buildUrl(URLS.STORAGE_URL, "img/home/pending_icon.png").toString()}
          // preview={false}
        />
        <Flex justify={"space-between"} vertical>
          <Typography.Title level={4}>{t("overview.completeProfile.pendingShaparak.title")}</Typography.Title>
          <Typography.Text type={"secondary"}>
            {t("overview.completeProfile.pendingShaparak.description")}
          </Typography.Text>
        </Flex>
      </Flex>
    </LegacyCard.Ant>
  );
  const desktopCard = (
    <LegacyCard.Ant className={"tw:w-full"}>
      <Flex vertical className="tw:w-full">
        <Steps
          type="navigation"
          size="small"
          current={currentStep}
          onChange={(cur) => setCurrentStep(cur)}
          className="site-navigation-steps"
          items={[
            {
              title: t("overview.completeProfile.identityInformation.title"),
              subTitle: itemSubTitle(personalSectionStatus),
              icon: itemIcon(personalSectionStatus),
            },
            {
              title: t("overview.completeProfile.businessInformation.title"),
              subTitle: itemSubTitle(businessSectionStatus),
              icon: itemIcon(businessSectionStatus),
            },
            {
              title: t("overview.completeProfile.bankInformation.title"),
              subTitle: bankItemSubTitle(bankSectionStatus),
              icon: bankItemIcon(bankSectionStatus),
            },
          ]}
        />
        {currentStep === StepsEnum.identityInformation ? identityInformationContent : null}
        {currentStep === StepsEnum.businessInformation ? businessInformationContent : null}
        {currentStep === StepsEnum.bankInformation ? bankInformationContent : null}
      </Flex>
    </LegacyCard.Ant>
  );
  const mobileCard = (
    <LegacyCard.Ant
      className={"tw:w-full"}
      styles={{
        body: {
          padding: 0,
        },
      }}
    >
      <Collapse
        bordered={false}
        ghost
        expandIconPlacement="end"
        activeKey={currentStep}
        onChange={(key) => {
          if (key[1]) {
            setCurrentStep(Number(key[1]));
          }
        }}
        items={[
          {
            key: StepsEnum.identityInformation,
            label: (
              <Flex align="center" gap="4px">
                {itemIcon(personalSectionStatus)}
                <Typography.Title className="tw:m-0!" level={5}>
                  {t("overview.completeProfile.identityInformation.title")}
                </Typography.Title>
                <Typography.Text className="color--gray500">{itemSubTitle(personalSectionStatus)}</Typography.Text>
              </Flex>
            ),
            children: identityInformationContent,
            className: "border-bottom border--gray100",
          },
          {
            key: StepsEnum.businessInformation,
            label: (
              <Flex align="center" gap="4px">
                {itemIcon(businessSectionStatus)}
                <Typography.Title className="tw:m-0!" level={5}>
                  {t("overview.completeProfile.businessInformation.title")}
                </Typography.Title>
                <Typography.Text className="color--gray500">{itemSubTitle(businessSectionStatus)}</Typography.Text>
              </Flex>
            ),
            children: businessInformationContent,
            className: "border-bottom border--gray100",
          },
          {
            key: StepsEnum.bankInformation,
            label: (
              <Flex align="center" gap="4px">
                {bankItemIcon(bankSectionStatus)}
                <Typography.Title className="tw:m-0!" level={5}>
                  {t("overview.completeProfile.bankInformation.title")}
                </Typography.Title>
                <Typography.Text className="color--gray500">{bankItemSubTitle(bankSectionStatus)}</Typography.Text>
              </Flex>
            ),
            children: bankInformationContent,
          },
        ]}
      />
    </LegacyCard.Ant>
  );

  const shaparakRejectedCard = (
    <LegacyCard.Ant className={"tw:w-full"}>
      <Flex align={"center"} gap={"small"}>
        <img
          alt={"Pending Shaparak"}
          width={80}
          height={80}
          src={buildUrl(URLS.STORAGE_URL, "img/home/shaparakRejected.png").toString()}
          // preview={false}
        />
        <Flex justify={"space-between"} vertical>
          <Typography.Title level={4}>{t("overview.completeProfile.shaparakRejected.title")}</Typography.Title>
          <Typography.Text type={"secondary"}>
            {t("overview.completeProfile.shaparakRejected.description", { rejectedReason: wizard?.reason })}
          </Typography.Text>
        </Flex>
      </Flex>
    </LegacyCard.Ant>
  );
  if (loading) return <Skeleton active paragraph />;

  if (haveTerminal && !hasValidContract) {
    return <UserSignContract />;
  }

  if (hideSection) return null;

  if (lastWithdarwState.value) return null;

  if (wizard?.userInfoStatus === WizardStatusEnum.ShaparakPending) return pendingTerminalCard;

  if (wizard?.userInfoStatus === WizardStatusEnum.ShaparakRejected) return shaparakRejectedCard;

  if (haveTerminal) return successfulTerminalCard;

  return xl ? desktopCard : mobileCard;
};

export default CompleteProfile;
